import { BRAND } from '@console/shared'
import type { ReactNode } from 'react'
import { BrandGround } from '../../components/brand/BrandGround'
import { Wordmark } from '../../components/layout/Logo'

/**
 * The sign-in, sign-up and invite pages, laid out like app.trodo.ai's: the Trodo range along the bottom, the wordmark
 * top-left, the product's promise beside a glass card on wide screens, and one line under the range.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  product,
  pitch = true,
}: {
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /** Wordmark label, e.g. "Admin" for the admin app (default: Console). */
  product?: string
  /** The product pitch beside the card on wide screens (the admin app's sign-in has none). */
  pitch?: boolean
}) {
  return (
    <div className="relative flex h-screen w-full flex-col overflow-y-auto overflow-x-hidden">
      <BrandGround />
      <div className="relative z-10 shrink-0 px-7 pt-6">
        <a href="/login" aria-label={product ? `Trodo ${product}` : 'Trodo Console'}>
          <Wordmark {...(product ? { product } : {})} />
        </a>
      </div>
      <div className="relative z-10 flex flex-1 items-start justify-center px-4 pb-8 pt-[7vh] lg:items-center lg:pb-[14vh] lg:pt-0">
        <div className={`flex w-full max-w-[1080px] items-center justify-center gap-16 ${pitch ? 'lg:justify-between' : ''}`}>
          {pitch && (
            <div className="hidden min-w-0 flex-1 lg:block">
              <Pitch />
            </div>
          )}
          <div className="auth-glass w-full max-w-[400px] shrink-0 animate-fade-in rounded-2xl border border-line px-8 pb-8 pt-9">
            <h1 className="text-[28px] font-medium leading-tight tracking-[-0.025em] text-ink">{title}</h1>
            {subtitle && <p className="mt-2 text-[13.5px] leading-relaxed text-ink-muted">{subtitle}</p>}
            <div className="mt-7">{children}</div>
            {footer && <div className="mt-6 border-t border-line pt-5 text-center text-[12.5px] text-ink-muted">{footer}</div>}
          </div>
        </div>
      </div>
      <div className="relative z-10 shrink-0 px-4 pb-6 text-center">
        <p className="text-[16px] leading-snug tracking-[-0.01em] text-ink sm:text-[19px]">{BRAND.tagline}, at a fraction of the cost.</p>
      </div>
      <style>{`
        .auth-glass {
          background: rgba(255, 255, 255, 0.86);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          box-shadow: 0 8px 32px -8px rgba(60, 40, 20, 0.12), 0 1px 0 rgba(255, 255, 255, 0.6) inset;
        }
        :root[data-theme="dark"] .auth-glass {
          background: rgba(20, 19, 18, 0.82);
          box-shadow: 0 8px 32px -8px rgba(0, 0, 0, 0.45), 0 1px 0 rgba(255, 255, 255, 0.04) inset;
        }
      `}</style>
    </div>
  )
}

/** What the console is for, in trodo.ai's two-tone voice, with the three things you do here. */
function Pitch() {
  return (
    <div className="max-w-[560px]">
      <p className="eyebrow">{BRAND.modelFamily} · decision models</p>
      <h2 className="headline mt-4 text-[40px] leading-[1.08]">
        <span className="pre">Every agent decision, judged.</span>
        In one forward pass.
      </h2>
      <p className="mt-5 text-[15px] leading-relaxed text-ink-secondary">
        Ask typed questions about any trace, conversation or tool catalogue and get a calibrated probability for every
        option, including “can't tell”, or pull out the exact value it states. No text to parse.
      </p>
      <ol className="mt-7 space-y-3 text-[14px] text-ink">
        {['Get an API key', 'Try it in the playground', 'Pay only for input tokens'].map((t, i) => (
          <li key={t} className="flex items-center gap-3">
            <span className="flex h-5 w-5 items-center justify-center rounded-full border border-line font-mono text-[10.5px] text-ink-muted">{i + 1}</span>
            {t}
          </li>
        ))}
      </ol>
    </div>
  )
}

export function GoogleButton({ onClick, label = 'Continue with Google', loading }: { onClick: () => void; label?: string; loading?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={loading} className="btn-secondary h-10 w-full gap-2 !text-[13.5px]">
      <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
      {label}
    </button>
  )
}

export function Divider() {
  return (
    <div className="my-4 flex items-center gap-3 text-[11px] text-ink-disabled">
      <span className="h-px flex-1 bg-[color:var(--border-subtle)]" />
      or
      <span className="h-px flex-1 bg-[color:var(--border-subtle)]" />
    </div>
  )
}

/** Turn Better Auth / gateway errors into one sentence. */
export function authErrorMessage(err: { code?: string; message?: string } | null | undefined): string | null {
  if (!err) return null
  const code = err.code ?? ''
  if (code === 'BETA_INVITE_REQUIRED' || /invite-only|BETA/i.test(err.message ?? ''))
    return `${BRAND.consoleName} is in private beta and this email isn't in yet. Join the waitlist and we'll email you when you're in.`
  if (code === 'EMAIL_NOT_VERIFIED') return 'Verify your email first: we sent you a link when you signed up.'
  if (code === 'INVALID_EMAIL_OR_PASSWORD') return 'Wrong email or password.'
  if (code === 'USER_ALREADY_EXISTS' || code === 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL') return 'An account with this email already exists. Sign in instead.'
  if (code === 'PASSWORD_TOO_SHORT') return 'Use at least 10 characters for your password.'
  return err.message || 'Something went wrong. Try again.'
}
